import React, { useRef, useState } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Button, CrossIcon } from '@motorway/mw-highway-code';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';

import { FLAG } from 'Utilities/analytics/optimizely/constants';
import { useCoarsePointer } from 'Utilities/helpers';
import useLockScroll from 'Utilities/hooks/useLockScroll';
import useInView from 'Utilities/useInView';

import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import { useBinaryExperiment } from '../../../experiments/useBinaryExperiment';
import LocalTexts from '../ulezText.json';

import {
  ULEZ_CENTRAL_BOUNDS,
  ULEZ_INNER_BOUNDS,
  ULEZ_OUTER_BOUNDS,
} from './coordinates';
import {
  API_KEY,
  BLUE,
  BLUE_OPACITY,
  CLOSE_EXPANDED_MAP_VIEW_ANIMATION_LENGTH,
  EXPANDED_MAP_CONTROLS,
  GREEN,
  GREEN_OPACITY,
  MAP_CONTROLS,
  MAP_ID,
  MAP_OPTIONS,
  ORANGE,
  ORANGE_DARK,
  ORANGE_OPACITY,
} from './helpers';
import { useAdStorageConsent, useResponsiveMapOptions } from './hooks';
import { MapLegend } from './MapLegend';
import { MapPlaceholder } from './MapPlaceholder';

import styles from './Map.scss';

const LocalT = new MDText(LocalTexts);

type MapProps = {
  expandedMap?: boolean;
  setExpandedMap?: (expanded: boolean) => void;
}

type MapItemProps = {
  children?: React.ReactNode;
}

const POLYGONS_DATA = [
  {
    bounds: ULEZ_INNER_BOUNDS,
    id: 'ulezInnerBounds',
    styles: {
      fillColor: BLUE,
      fillOpacity: BLUE_OPACITY,
      strokeColor: BLUE,
      strokeWeight: 2,
      zIndex: 2,
    },
  },
  {
    bounds: ULEZ_CENTRAL_BOUNDS,
    id: 'ulezCentralBounds',
    styles: {
      fillColor: ORANGE,
      fillOpacity: ORANGE_OPACITY,
      strokeColor: ORANGE_DARK,
      strokeWeight: 2,
      zIndex: 3,
    },
  },
  {
    boundsEncoded: ULEZ_OUTER_BOUNDS,
    id: 'ulezOuterBounds',
    styles: {
      fillColor: GREEN,
      fillOpacity: GREEN_OPACITY,
      strokeColor: GREEN,
      strokeWeight: 2,
    },
  },
];

const Loading = () => (
  <div className={styles.loading}>
    <LoadingArrow />
  </div>
);

const InteractiveMap = ({ expandedMap, setExpandedMap }: MapProps) => {
  const [libraries] = useState<any | undefined>(['geometry']);
  const isCoarsePointer = useCoarsePointer();

  const setIsLocked = useLockScroll();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    id: MAP_ID,
    libraries,
  });

  const { center, latLngBounds, zoom } = useResponsiveMapOptions(expandedMap);

  const options = {
    ...MAP_OPTIONS,
    defaultZoom: zoom,
    restriction: { latLngBounds },
    ...(isCoarsePointer && (!expandedMap ? MAP_CONTROLS : EXPANDED_MAP_CONTROLS)),
  };

  const openFullScreen = () => {
    setExpandedMap?.(true);
    setIsLocked(true);
  };

  const closeFullScreen = () => {
    setTimeout(() => {
      setExpandedMap?.(false);
      setIsLocked(false);
    }, CLOSE_EXPANDED_MAP_VIEW_ANIMATION_LENGTH);
  };

  const MapItem = ({ children }: MapItemProps) => (
    <>
      {children}
      <GoogleMap
        { ...{ center, options, zoom }}
        mapContainerClassName={
          cx(styles.map, {
            [styles.expanded]: expandedMap,
          })
        }
      >
        {
          POLYGONS_DATA.map((p) => (
            <Polygon
              key={p?.id}
              options={p?.styles as any}
              path={p?.bounds && p.bounds}
              paths={p?.boundsEncoded
                && p.boundsEncoded.map((b:any) => window?.google?.maps?.geometry?.encoding?.decodePath(b))}
            />
          ))
        }
        <MapLegend { ...{ expandedMap }} onClick={openFullScreen} />
      </GoogleMap>
    </>
  );

  return (
    <>
      {
        isLoaded ? (
          <MapItem>
            {expandedMap && (
              <Button
                data-thc-button="close"
                icon={CrossIcon}
                label={LocalT.translate('map.close')}
                onClick={closeFullScreen}
                showLabel={false}
                type="button"
                variant="secondary"
              />
            )}
          </MapItem>
        ) : (<Loading />)
      }
    </>
  );
};

const MapContent = ({ expandedMap, setExpandedMap }: MapProps) => {
  const hasAdStorageConsent = useAdStorageConsent();
  const showCookiePreferences = useBinaryExperiment(FLAG.COOKIE_CONSENT_BANNER, {
    triggerExperiment: false,
  });

  if (!hasAdStorageConsent && showCookiePreferences) {
    return <MapPlaceholder />;
  }

  return <InteractiveMap { ...{ expandedMap, setExpandedMap } } />;
};

export const Map = ({ expandedMap, setExpandedMap }: MapProps) => {
  const $container = useRef<HTMLDivElement>(null);

  const visible = useInView($container, {
    disconnectOnVisible: true,
    rootMargin: '60px',
  });

  return (
    <div ref={$container} className={cx(styles.component)}>
      {
        (visible)
          ? (<MapContent { ...{ expandedMap, setExpandedMap } }/>)
          : (<Loading />)
      }
    </div>
  );
};
